import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Link as LinkIcon,
  Copy,
  Check,
  Smartphone,
  Globe,
  Apple,
  Bug,
  ArrowLeft,
  ArrowRight,
  ImageIcon,
  Tag,
  CalendarClock,
  Lock,
} from 'lucide-react';
import { useForm } from '../../hooks/useForm';
import { useApi } from '../../hooks/useApi';
import { linksAPI } from '../../services/api';
import { copyToClipboard, generateShortUrl } from '../../utils/helpers';
import { SHORT_LINK_BASE_URL } from '../../config';
import Button from '../common/Button';
import Input from '../common/Input';
import Textarea from '../common/Textarea';
import Alert from '../common/Alert';
import LoadingSpinner from '../common/LoadingSpinner';

const Section = ({ icon: Icon, title, description, action, children }) => (
  <section className="rounded-2xl border border-ink-200 bg-white shadow-soft p-6 sm:p-7">
    <header className="flex items-start justify-between gap-4 mb-5">
      <div className="flex items-start gap-3 min-w-0">
        {Icon && (
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-200/60 shrink-0">
            <Icon className="h-5 w-5" />
          </span>
        )}
        <div className="min-w-0">
          <h2 className="text-base font-semibold tracking-tight text-ink-900">
            {title}
          </h2>
          {description && (
            <p className="text-sm text-ink-500 mt-0.5">{description}</p>
          )}
        </div>
      </div>
      {action}
    </header>
    {children}
  </section>
);

const CreateLinkForm = () => {
  const navigate = useNavigate();
  const { loading, error, executeApiCall, clearError } = useApi();
  const [createdLink, setCreatedLink] = useState(null);
  const [copied, setCopied] = useState(null);
  const [showMobileConfig, setShowMobileConfig] = useState(false);

  const validationRules = {
    originalUrl: { required: true, url: true, label: 'Destination URL' },
    title: { maxLength: 255, label: 'Title' },
    description: { maxLength: 1000, label: 'Description' },
    imageUrl: { url: true, label: 'Image URL' },
    androidPackageName: { maxLength: 100, label: 'Android Package Name' },
    androidFallbackUrl: { url: true, label: 'Android Fallback URL' },
    iosBundleId: { maxLength: 100, label: 'iOS Bundle ID' },
    iosFallbackUrl: { url: true, label: 'iOS Fallback URL' },
  };

  const initialValues = {
    originalUrl: '',
    customAlias: '',
    title: '',
    description: '',
    imageUrl: '',
    tags: '',
    expiresAt: '',
    activeFrom: '',
    maxClicks: '',
    password: '',
    androidPackageName: '',
    androidFallbackUrl: '',
    iosBundleId: '',
    iosFallbackUrl: '',
    iosAppStoreId: '',
    androidAppStoreId: '',
  };

  const { values, errors, touched, handleChange, handleBlur, validate, reset } =
    useForm(initialValues, validationRules);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const result = await executeApiCall(linksAPI.create, values);
    if (result.success) setCreatedLink(result.data);
  };

  const handleCopyToClipboard = async (text, key) => {
    const result = await copyToClipboard(text);
    if (result.success) {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    }
  };

  const handleCreateAnother = () => {
    setCreatedLink(null);
    reset();
    clearError();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <LoadingSpinner size="large" text="Creating your link…" />
      </div>
    );
  }

  if (createdLink) {
    const shortUrl =
      createdLink.shortUrl ||
      generateShortUrl(createdLink.shortCode, SHORT_LINK_BASE_URL);
    const debugUrl = `${shortUrl}?d=1`;

    return (
      <div className="max-w-2xl mx-auto">
        <div className="rounded-3xl border border-ink-200 bg-white shadow-card overflow-hidden">
          <div className="px-8 pt-10 text-center">
            <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 text-white shadow-soft">
              <Check className="h-7 w-7" />
            </div>
            <h2 className="mt-5 text-2xl font-bold tracking-tight text-ink-900">
              Your link is live
            </h2>
            <p className="mt-1.5 text-sm text-ink-500">
              Share it anywhere. We'll route every click for you.
            </p>
          </div>

          <div className="px-8 py-8 space-y-4">
            {/* Short URL */}
            <div className="rounded-xl border border-ink-200 bg-ink-50/50 p-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-ink-500">
                  Short link
                </span>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => handleCopyToClipboard(shortUrl, 'short')}
                >
                  {copied === 'short' ? (
                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                  {copied === 'short' ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <div className="font-mono text-sm text-ink-900 break-all">
                {shortUrl}
              </div>
            </div>

            {/* Debug URL */}
            <div className="rounded-xl border border-ink-200 bg-white p-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-amber-600">
                  <Bug className="h-3.5 w-3.5" /> Debug link
                </span>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => handleCopyToClipboard(debugUrl, 'debug')}
                >
                  {copied === 'debug' ? (
                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                  {copied === 'debug' ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <div className="font-mono text-sm text-ink-700 break-all">
                {debugUrl}
              </div>
              <p className="mt-2 text-xs text-ink-500">
                Add <code className="text-ink-700">?d=1</code> to any link to see
                platform detection details.
              </p>
            </div>

            {/* Meta */}
            <dl className="grid sm:grid-cols-2 gap-3 text-sm pt-2">
              <div>
                <dt className="text-xs uppercase tracking-wider text-ink-500">
                  Destination
                </dt>
                <dd className="mt-0.5 text-ink-800 break-all">
                  {createdLink.originalUrl}
                </dd>
              </div>
              {createdLink.title && (
                <div>
                  <dt className="text-xs uppercase tracking-wider text-ink-500">
                    Title
                  </dt>
                  <dd className="mt-0.5 text-ink-800">{createdLink.title}</dd>
                </div>
              )}
              {createdLink.androidPackageName && (
                <div>
                  <dt className="text-xs uppercase tracking-wider text-ink-500">
                    Android package
                  </dt>
                  <dd className="mt-0.5 text-ink-800 font-mono text-xs">
                    {createdLink.androidPackageName}
                  </dd>
                </div>
              )}
              {createdLink.iosBundleId && (
                <div>
                  <dt className="text-xs uppercase tracking-wider text-ink-500">
                    iOS bundle ID
                  </dt>
                  <dd className="mt-0.5 text-ink-800 font-mono text-xs">
                    {createdLink.iosBundleId}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          <div className="border-t border-ink-100 px-8 py-5 flex flex-wrap gap-3 justify-end">
            <Button variant="secondary" onClick={handleCreateAnother}>
              Create another
            </Button>
            <Button variant="primary" onClick={() => navigate('/links')}>
              View all links
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-500 hover:text-ink-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <h1 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-ink-900">
          Create a new link
        </h1>
        <p className="mt-1.5 text-sm text-ink-500">
          A short URL with optional metadata and mobile app routing.
        </p>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={clearError}
          className="mb-5"
        />
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Section
          icon={Globe}
          title="Destination"
          description="Where should this link send people?"
        >
          <div className="space-y-4">
            <Input
              label="Destination URL"
              name="originalUrl"
              type="url"
              value={values.originalUrl}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.originalUrl}
              touched={touched.originalUrl}
              placeholder="https://example.com/your-page"
              required
              icon={LinkIcon}
            />
            <div>
              <Input
                label="Custom alias (optional)"
                name="customAlias"
                type="text"
                value={values.customAlias}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.customAlias}
                touched={touched.customAlias}
                placeholder="my-campaign"
              />
              <p className="mt-1.5 text-xs text-ink-500">
                Leave blank for a random code. 3–30 letters, numbers, hyphens or
                underscores. Your link will be{' '}
                <code className="text-ink-700">{`${SHORT_LINK_BASE_URL}/r/${values.customAlias || '…'}`}</code>
              </p>
            </div>
          </div>
        </Section>

        <Section
          icon={ImageIcon}
          title="Preview (optional)"
          description="Title, description and image shown when this link is unfurled on social."
        >
          <div className="space-y-4">
            <Input
              label="Title"
              name="title"
              type="text"
              value={values.title}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.title}
              touched={touched.title}
              placeholder="e.g. New product launch"
            />
            <Textarea
              label="Description"
              name="description"
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.description}
              touched={touched.description}
              placeholder="A short summary of what this link is for…"
              rows={3}
            />
            <Input
              label="Image URL"
              name="imageUrl"
              type="url"
              value={values.imageUrl}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.imageUrl}
              touched={touched.imageUrl}
              placeholder="https://example.com/og-image.jpg"
            />
          </div>
        </Section>

        <Section
          icon={CalendarClock}
          title="Schedule, limits & protection (optional)"
          description="Control when the link works, cap its clicks, protect it with a password, and tag it."
        >
          <div className="space-y-4">
            <Input
              label="Tags"
              name="tags"
              type="text"
              value={values.tags}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="marketing, q3, launch"
              icon={Tag}
            />
            <p className="-mt-2 text-xs text-ink-500">Comma-separated. Used for filtering your links.</p>

            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                label="Active from"
                name="activeFrom"
                type="datetime-local"
                value={values.activeFrom}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <Input
                label="Expires at"
                name="expiresAt"
                type="datetime-local"
                value={values.expiresAt}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                label="Max clicks"
                name="maxClicks"
                type="number"
                min="1"
                value={values.maxClicks}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Unlimited"
              />
              <Input
                label="Password"
                name="password"
                type="text"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Leave blank for none"
                icon={Lock}
              />
            </div>
          </div>
        </Section>

        <Section
          icon={Smartphone}
          title="Mobile deep linking"
          description="Optional — route users into your iOS or Android app, with a web fallback."
          action={
            <Button
              type="button"
              variant="ghost"
              size="small"
              onClick={() => setShowMobileConfig((v) => !v)}
            >
              {showMobileConfig ? 'Hide' : 'Configure'}
            </Button>
          }
        >
          {showMobileConfig ? (
            <div className="space-y-5">
              <div className="rounded-xl border border-ink-200 bg-ink-50/40 p-5">
                <h3 className="text-sm font-semibold text-ink-900 mb-4 flex items-center gap-2">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-emerald-100 text-emerald-700">
                    <Smartphone className="h-3.5 w-3.5" />
                  </span>
                  Android
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Package name"
                    name="androidPackageName"
                    value={values.androidPackageName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.androidPackageName}
                    touched={touched.androidPackageName}
                    placeholder="com.example.app"
                  />
                  <Input
                    label="Fallback URL"
                    name="androidFallbackUrl"
                    type="url"
                    value={values.androidFallbackUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.androidFallbackUrl}
                    touched={touched.androidFallbackUrl}
                    placeholder="https://play.google.com/…"
                  />
                  <Input
                    label="Play Store ID (optional)"
                    name="androidAppStoreId"
                    value={values.androidAppStoreId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.androidAppStoreId}
                    touched={touched.androidAppStoreId}
                    placeholder="com.example.app"
                  />
                </div>
              </div>

              <div className="rounded-xl border border-ink-200 bg-ink-50/40 p-5">
                <h3 className="text-sm font-semibold text-ink-900 mb-4 flex items-center gap-2">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-sky-100 text-sky-700">
                    <Apple className="h-3.5 w-3.5" />
                  </span>
                  iOS
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    label="Bundle ID"
                    name="iosBundleId"
                    value={values.iosBundleId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.iosBundleId}
                    touched={touched.iosBundleId}
                    placeholder="com.example.app"
                  />
                  <Input
                    label="Fallback URL"
                    name="iosFallbackUrl"
                    type="url"
                    value={values.iosFallbackUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.iosFallbackUrl}
                    touched={touched.iosFallbackUrl}
                    placeholder="https://apps.apple.com/…"
                  />
                  <Input
                    label="App Store ID (optional)"
                    name="iosAppStoreId"
                    value={values.iosAppStoreId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={errors.iosAppStoreId}
                    touched={touched.iosAppStoreId}
                    placeholder="123456789"
                  />
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-ink-500">
              Want links that open your apps when installed?{' '}
              <button
                type="button"
                onClick={() => setShowMobileConfig(true)}
                className="font-medium text-brand-600 hover:text-brand-700"
              >
                Configure deep linking
              </button>
              .
            </p>
          )}
        </Section>

        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/links')}
          >
            Cancel
          </Button>
          <Button type="submit" loading={loading} variant="primary">
            Create link
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateLinkForm;
